import { Asset } from "expo-asset";
import { useState, useEffect } from "react";
import {
    StyleSheet,
    View,
    TouchableOpacity,
    Image,
    ActivityIndicator,
    Modal,
    ScrollView
} from "react-native";
import { Text, Icon } from "react-native-paper";

import { UploadStagingModal } from "./UploadStagingModal";
import { VerificationStatus } from "./VerificationStatus";

import { useUploadFlow } from "@/hooks/useUploadFlow";
import { logRocketRedactProps } from "@/lib/logrocket";
import { submitUpload, confirmUpload } from "@/lib/onboarding-api";
import { colors } from "@/lib/theme";
import { uploadFromUri } from "@/lib/upload-from-uri";
import { ONBOARDING_MAX_DIMENSION } from "@/utils/resize-image";

interface DocumentUploadProps {
    rnId: string;
    submissionId?: string;
    itemName?: string;
    status?: string;
    rejectionReason?: string | null;
    existingUploadUrl?: string;
    onUploaded?: () => void;
    /** When set, upload is blocked and this reason is shown (e.g. a required
     *  field must be filled first). */
    disabledReason?: string;
}

type UploadState = "idle" | "picking" | "uploading" | "verifying" | "done";

// All test docs with category tags for filtering
const ALL_TEST_DOCS = [
    // Photo ID
    { label: "✅ Photo ID (Valid)", file: "photo-id-valid.png", tags: ["photo id", "photo", "id"] },
    {
        label: "❌ Photo ID (Expired)",
        file: "photo-id-expired.png",
        tags: ["photo id", "photo", "id"]
    },
    // Proof of Work
    {
        label: "✅ Employment Letter",
        file: "proof-of-work-valid.png",
        tags: ["proof", "employment", "work", "reference"]
    },
    {
        label: "✅ Pay Stub",
        file: "proof-of-work-paystub.png",
        tags: ["proof", "employment", "work", "paystub"]
    },
    // Flu Vaccine
    {
        label: "✅ Flu Vaccine (Current Season)",
        file: "flu-vaccine-valid.png",
        tags: ["flu", "vaccine", "influenza"]
    },
    {
        label: "❌ Flu Vaccine (Old Season)",
        file: "flu-vaccine-old-season.png",
        tags: ["flu", "vaccine", "influenza"]
    },
    // Hep B
    {
        label: "✅ Hep B Vaccine (Complete)",
        file: "hep-b-vaccine-valid.png",
        tags: ["hep", "hepatitis", "vaccine"]
    },
    // Insurance
    {
        label: "✅ Insurance ($1M/$3M)",
        file: "insurance-valid.png",
        tags: ["insurance", "liability"]
    },
    {
        label: "❌ Insurance (Expired)",
        file: "insurance-expired.png",
        tags: ["insurance", "liability"]
    },
    {
        label: "⚠️ Insurance (Low Coverage)",
        file: "insurance-low-coverage.png",
        tags: ["insurance", "liability"]
    },
    // Child Abuse
    {
        label: "✅ Child Abuse Cert (Valid)",
        file: "child-abuse-cert-valid.png",
        tags: ["child abuse", "certification", "course"]
    },
    {
        label: "❌ Child Abuse Cert (Expired)",
        file: "child-abuse-cert-expired.png",
        tags: ["child abuse", "certification", "course"]
    },
    // Edge cases
    { label: "❌ Wrong Document (Phone Bill)", file: "wrong-document.png", tags: ["wrong"] },
    { label: "❌ Blurry Document", file: "blurry-document.png", tags: ["blurry"] }
];

/* eslint-disable @typescript-eslint/no-require-imports */
const TEST_DOC_ASSETS: Record<string, number> = {
    "photo-id-valid.png": require("@/assets/test-docs/photo-id-valid.png"),
    "photo-id-expired.png": require("@/assets/test-docs/photo-id-expired.png"),
    "proof-of-work-valid.png": require("@/assets/test-docs/proof-of-work-valid.png"),
    "proof-of-work-paystub.png": require("@/assets/test-docs/proof-of-work-paystub.png"),
    "flu-vaccine-valid.png": require("@/assets/test-docs/flu-vaccine-valid.png"),
    "flu-vaccine-old-season.png": require("@/assets/test-docs/flu-vaccine-old-season.png"),
    "hep-b-vaccine-valid.png": require("@/assets/test-docs/hep-b-vaccine-valid.png"),
    "insurance-valid.png": require("@/assets/test-docs/insurance-valid.png"),
    "insurance-expired.png": require("@/assets/test-docs/insurance-expired.png"),
    "insurance-low-coverage.png": require("@/assets/test-docs/insurance-low-coverage.png"),
    "child-abuse-cert-valid.png": require("@/assets/test-docs/child-abuse-cert-valid.png"),
    "child-abuse-cert-expired.png": require("@/assets/test-docs/child-abuse-cert-expired.png"),
    "wrong-document.png": require("@/assets/test-docs/wrong-document.png"),
    "blurry-document.png": require("@/assets/test-docs/blurry-document.png")
};
/* eslint-enable @typescript-eslint/no-require-imports */

// Test/sample-doc injector disabled — always use the real capture/upload flow.
const DEV_MODE = false;

function getRelevantDocs(itemName?: string): typeof ALL_TEST_DOCS {
    if (!itemName) {
        return ALL_TEST_DOCS;
    }
    const lower = itemName.toLowerCase();
    // Find docs whose tags match the item name
    const matched = ALL_TEST_DOCS.filter(
        (d) =>
            d.tags.some((tag) => lower.includes(tag)) ||
            d.tags.includes("wrong") ||
            d.tags.includes("blurry")
    );
    return matched.length > 2 ? matched : ALL_TEST_DOCS; // fallback to all if no good matches
}

export function DocumentUpload({
    rnId,
    submissionId,
    itemName,
    status,
    rejectionReason,
    existingUploadUrl,
    onUploaded,
    disabledReason
}: DocumentUploadProps) {
    const [state, setState] = useState<UploadState>(
        status === "ai_verifying"
            ? "verifying"
            : status === "approved"
              ? "done"
              : status === "submitted" && existingUploadUrl
                ? "verifying"
                : "idle"
    );
    const [imageUri, setImageUri] = useState<string | null>(existingUploadUrl ?? null);
    const [error, setError] = useState<string | null>(null);
    const [showDevPicker, setShowDevPicker] = useState(false);

    useEffect(() => {
        if (status === "rejected" || status === "verification_failed") {
            setState("idle");
        } else if (status === "approved") {
            setState("done");
        } else if (status === "ai_verifying") {
            setState("verifying");
        }
    }, [status]);

    // Sync image preview when a new upload URL arrives via polling
    useEffect(() => {
        if (existingUploadUrl) {
            setImageUri(existingUploadUrl);
        }
    }, [existingUploadUrl]);

    // Upload flow with staging modal for multi-capture/multi-select
    const uploadFlow = useUploadFlow({
        rnId,
        submissionId,
        //  Without this an ordinary phone photo goes up at full resolution and
        //  the document reader refuses it over 5 MB — with a message the nurse
        //  cannot act on, because the file size is not hers to change.
        maxDimension: ONBOARDING_MAX_DIMENSION,
        onComplete: () => {
            setState("verifying");
            onUploaded?.();
        }
    });

    async function handleDevDocSelect(doc: (typeof ALL_TEST_DOCS)[0]) {
        setShowDevPicker(false);
        setState("uploading");
        setError(null);

        try {
            const assetModule = TEST_DOC_ASSETS[doc.file];
            const [loadedAsset] = await Asset.loadAsync(assetModule);
            const uri = loadedAsset.localUri ?? loadedAsset.uri;

            const { upload, uploadUrl } = await submitUpload(rnId, {
                filename: doc.file,
                contentType: "image/png",
                submissionId
            });

            // Not a Blob through fetch: expo/fetch (SDK 56+) would replace the
            // signed Content-Type with the Blob's own type and S3 would refuse it.
            await uploadFromUri({ uri, uploadUrl, contentType: "image/png" });

            await confirmUpload(rnId, upload.id);
            setState("verifying");
            onUploaded?.();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Upload failed");
            setState("idle");
        }
    }

    if (state === "verifying" || (state === "done" && status)) {
        return (
            <View style={styles.container}>
                {imageUri && (
                    <Image
                        {...logRocketRedactProps()}
                        source={{ uri: imageUri }}
                        style={styles.preview}
                    />
                )}
                <VerificationStatus
                    status={status ?? "ai_verifying"}
                    rejectionReason={rejectionReason}
                />
            </View>
        );
    }

    const relevantDocs = getRelevantDocs(itemName);

    return (
        <View style={styles.container}>
            {imageUri && (
                <Image
                    {...logRocketRedactProps()}
                    source={{ uri: imageUri }}
                    style={styles.preview}
                />
            )}

            {/* Show status badge when rejected or verification_failed */}
            {(status === "rejected" || status === "verification_failed") && (
                <VerificationStatus status={status} rejectionReason={rejectionReason} />
            )}

            {state === "uploading" ? (
                <View style={styles.uploadingRow}>
                    <ActivityIndicator size="small" color={colors.primary} />
                    <Text style={styles.uploadingText}>Uploading...</Text>
                </View>
            ) : disabledReason ? (
                <View style={styles.disabledHint}>
                    <Icon source="information-outline" size={16} color={colors.textTertiary} />
                    <Text style={styles.disabledHintText}>{disabledReason}</Text>
                </View>
            ) : (
                <View style={styles.buttonRow}>
                    {DEV_MODE && (
                        <TouchableOpacity
                            style={[styles.uploadButton, styles.devButton]}
                            onPress={() => setShowDevPicker(true)}
                        >
                            <Icon source="test-tube" size={18} color="#FFFFFF" />
                            <Text style={styles.uploadButtonText}>Test</Text>
                        </TouchableOpacity>
                    )}
                    <TouchableOpacity style={styles.uploadButton} onPress={uploadFlow.openCamera}>
                        <Icon source="camera" size={18} color="#FFFFFF" />
                        <Text style={styles.uploadButtonText}>Camera</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.uploadButton, styles.uploadButtonSecondary]}
                        onPress={uploadFlow.openGallery}
                    >
                        <Icon source="image-outline" size={18} color={colors.primary} />
                        <Text style={[styles.uploadButtonText, { color: colors.primary }]}>
                            Gallery
                        </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.uploadButton, styles.uploadButtonSecondary]}
                        onPress={uploadFlow.openDocument}
                    >
                        <Icon source="file-pdf-box" size={18} color={colors.primary} />
                        <Text style={[styles.uploadButtonText, { color: colors.primary }]}>
                            PDF
                        </Text>
                    </TouchableOpacity>
                </View>
            )}

            {error && <Text style={styles.errorText}>{error}</Text>}

            {/* Staging modal for multi-image / multi-PDF upload */}
            <UploadStagingModal
                visible={uploadFlow.showStaging}
                stagedImages={uploadFlow.stagedImages}
                onAddMoreCamera={uploadFlow.openCamera}
                onAddMoreGallery={uploadFlow.openGallery}
                onAddMorePdf={uploadFlow.openDocument}
                onRemove={uploadFlow.removeStaged}
                onUpload={uploadFlow.uploadAll}
                onCancel={uploadFlow.cancelStaging}
                uploading={uploadFlow.uploading}
                uploadProgress={uploadFlow.uploadProgress}
                uploadError={uploadFlow.uploadError}
            />

            {/* Dev picker modal */}
            {DEV_MODE && (
                <Modal visible={showDevPicker} transparent animationType="slide">
                    <View style={styles.modalOverlay}>
                        <View style={styles.modalContent}>
                            <View style={styles.modalHeader}>
                                <Text style={styles.modalTitle}>
                                    Test: {itemName ?? "Document"}
                                </Text>
                                <TouchableOpacity onPress={() => setShowDevPicker(false)}>
                                    <Icon source="close" size={24} color={colors.textPrimary} />
                                </TouchableOpacity>
                            </View>
                            <ScrollView style={styles.modalScroll}>
                                {relevantDocs.map((doc) => (
                                    <TouchableOpacity
                                        key={doc.file}
                                        style={styles.docItem}
                                        onPress={() => handleDevDocSelect(doc)}
                                    >
                                        <Icon
                                            source="file-document-outline"
                                            size={20}
                                            color={colors.primary}
                                        />
                                        <Text style={styles.docItemText}>{doc.label}</Text>
                                    </TouchableOpacity>
                                ))}
                            </ScrollView>
                        </View>
                    </View>
                </Modal>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        gap: 12
    },
    preview: {
        width: "100%",
        height: 200,
        borderRadius: 12,
        backgroundColor: colors.backgroundSecondary
    },
    buttonRow: {
        flexDirection: "row",
        gap: 8
    },
    disabledHint: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: colors.backgroundSecondary,
        borderRadius: 10,
        padding: 12
    },
    disabledHintText: {
        flex: 1,
        fontSize: 13,
        color: colors.textSecondary
    },
    uploadButton: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        backgroundColor: colors.primary,
        borderRadius: 10,
        paddingVertical: 14
    },
    uploadButtonSecondary: {
        backgroundColor: colors.primaryLight
    },
    devButton: {
        backgroundColor: "#7C3AED",
        flex: 0.6
    },
    uploadButtonText: {
        fontSize: 14,
        fontWeight: "600",
        color: "#FFFFFF"
    },
    uploadingRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 12
    },
    uploadingText: {
        fontSize: 13,
        color: colors.textSecondary
    },
    rejectionBox: {
        backgroundColor: colors.errorLight,
        borderRadius: 8,
        padding: 10
    },
    rejectionText: {
        fontSize: 12,
        color: colors.error
    },
    errorText: {
        fontSize: 12,
        color: colors.error,
        textAlign: "center"
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.5)",
        justifyContent: "flex-end"
    },
    modalContent: {
        backgroundColor: "#FFFFFF",
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        maxHeight: "60%",
        paddingBottom: 30
    },
    modalHeader: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 16,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.surfaceBorder
    },
    modalTitle: {
        fontSize: 16,
        fontWeight: "700",
        color: colors.textPrimary
    },
    modalScroll: {
        paddingHorizontal: 16
    },
    docItem: {
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        paddingVertical: 14,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.surfaceBorder
    },
    docItemText: {
        fontSize: 15,
        color: colors.textPrimary
    }
});
